import { useEffect, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { atomHardMode } from '../states';
import { atomLevel, atomActiveBallCount } from '../states/game.atoms';

// Economy-transparency panel: shows the EXACT outcome economy measured by full
// 10,000,000-seed enumeration (both modes). Loaded from /economy-stats.json.
type Bucket = { label: string; pct: number; loss?: boolean };
type ModeStats = {
  rtp: number; maxMult: number; trigger: number; cv: number; hitGe1: number; subStake: number; avgBalls: number;
  levels: { level: number; count: number }[]; dist: Bucket[];
};
type Stats = {
  seedSpace: number; nearMiss4of5: number; normal: ModeStats; hard: ModeStats;
  ceilingBand: [number, number]; rtpBand: [number, number];
};

const C = {
  panel: '#0e1a22', card: '#132129', line: '#1f3540', text: '#e8f0f2',
  dim: '#7fa0ad', accent: '#20fa20', gold: '#fcc002', bar: '#2b6d5f', barHot: '#fa013e',
};

export function StatsPanel() {
  const [s, setS] = useState<Stats | null>(null);
  // Panel mode toggle is the single source of truth for game mode too (PlaceYourBet is
  // hidden in the demo). Switching NORMAL/HARD here switches both the shown stats and the
  // economy the game plays.
  const [hardMode, setHardMode] = useAtom(atomHardMode);
  const mode: 'normal' | 'hard' = hardMode ? 'hard' : 'normal';
  const setMode = (k: 'normal' | 'hard') => setHardMode(k === 'hard');
  const level = useAtomValue(atomLevel);        // live game level 1..13
  const activeBalls = useAtomValue(atomActiveBallCount);
  useEffect(() => { fetch('/economy-stats.json').then(r => r.json()).then(setS).catch(() => {}); }, []);
  if (!s) return null;
  const m = s[mode];
  const maxPct = Math.max(...m.dist.map(d => d.pct));

  const Stat = ({ label, value, hint }: { label: string; value: string; hint?: string }) => (
    <div style={{ flex: 1, minWidth: 68 }}>
      <div style={{ color: C.dim, fontSize: 10, letterSpacing: 0.4 }}>{label}</div>
      <div style={{ color: C.text, fontSize: 17, fontWeight: 700 }}>{value}</div>
      {hint && <div style={{ color: C.dim, fontSize: 9 }}>{hint}</div>}
    </div>
  );

  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, height: '100vh', width: 300, zIndex: 40,
      background: C.panel, borderLeft: `1px solid ${C.line}`, color: C.text,
      fontFamily: 'ui-sans-serif, system-ui, sans-serif', padding: '14px 14px 20px',
      overflowY: 'auto', boxSizing: 'border-box',
    }}>
      <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: 1 }}>ECONOMY</div>
      <div style={{ color: C.dim, fontSize: 10, marginBottom: 12 }}>
        verified · full {(s.seedSpace / 1e6).toFixed(0)}M-seed enumeration · provably fair
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {(['normal', 'hard'] as const).map(k => (
          <button key={k} onClick={() => setMode(k)} style={{
            flex: 1, padding: '6px 0', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer',
            border: `1px solid ${mode === k ? C.accent : C.line}`,
            background: mode === k ? C.card : 'transparent', color: mode === k ? C.text : C.dim,
            textTransform: 'uppercase', letterSpacing: 0.5,
          }}>{k}{k === 'hard' ? ' · high-vol' : ''}</button>
        ))}
      </div>

      {level >= 2 && (
        <div style={{ background: '#16241c', border: `1px solid ${C.accent}`, borderRadius: 10, padding: '8px 12px', marginBottom: 12, textAlign: 'center' }}>
          <span style={{ color: C.accent, fontSize: 13, fontWeight: 800 }}>BONUS LEVEL {level}{level >= 13 ? ' · MAX' : ''}</span>
          <div style={{ color: C.dim, fontSize: 10, marginTop: 2 }}>×{2 ** (level - 1)} balls this level{activeBalls > 0 ? ` · ${activeBalls} in play` : ''}</div>
        </div>
      )}

      <div style={{ background: C.card, borderRadius: 10, padding: 12, border: `1px solid ${C.line}` }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <Stat label="RTP" value={`${m.rtp}%`} hint={`target ${s.rtpBand[0]}-${s.rtpBand[1]}%`} />
          <Stat label="MAX WIN" value={`${m.maxMult.toLocaleString()}×`} hint={`cap ${s.ceilingBand[0]}-${s.ceilingBand[1]}×`} />
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <Stat label="BONUS RATE" value={`${m.trigger}%`} />
          <Stat label="HIT ≥1×" value={`${m.hitGe1}%`} hint="genuine wins" />
          <Stat label="VOLATILITY" value={`${m.cv}`} hint="CV (rel.)" />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Stat label="LOSS < STAKE" value={`${m.subStake}%`} hint="not celebrated" />
          <Stat label="NEAR-MISS 4/5" value={`${s.nearMiss4of5}%`} hint="honest, verifiable" />
        </div>
      </div>

      <div style={{ fontSize: 11, color: C.dim, margin: '16px 0 8px', letterSpacing: 0.5 }}>PAYOUT DISTRIBUTION</div>
      <div style={{ background: C.card, borderRadius: 10, padding: 12, border: `1px solid ${C.line}` }}>
        {m.dist.filter(d => d.pct > 0).map(d => (
          <div key={d.label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
            <div style={{ width: 78, fontSize: 9.5, color: d.loss ? '#c77' : C.dim, textAlign: 'right' }}>{d.label}</div>
            <div style={{ flex: 1, height: 12, background: '#0a141a', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ width: `${Math.max(1.5, (d.pct / maxPct) * 100)}%`, height: '100%', background: d.loss ? '#5a2f36' : d.label.startsWith('10') || d.label.startsWith('5') ? C.gold : C.bar }} />
            </div>
            <div style={{ width: 40, fontSize: 10, color: C.text, textAlign: 'right' }}>{d.pct}%</div>
          </div>
        ))}
      </div>

      <div style={{ fontSize: 11, color: C.dim, margin: '16px 0 8px', letterSpacing: 0.5 }}>BONUS LADDER (reach rate)</div>
      <div style={{ background: C.card, borderRadius: 10, padding: 12, border: `1px solid ${C.line}`, fontSize: 10 }}>
        {m.levels.map(l => (
          <div key={l.level} style={{ display: 'flex', justifyContent: 'space-between', color: l.level >= 11 ? C.gold : C.dim, marginBottom: 2 }}>
            <span>L{l.level}{l.level === 13 ? ' · JACKPOT' : ''}</span>
            <span>{(l.count / s.seedSpace * 100).toPrecision(2)}%{l.count <= 40 ? ` (${l.count}/10M)` : ''}</span>
          </div>
        ))}
      </div>

      <div style={{ fontSize: 9, color: C.dim, marginTop: 14, lineHeight: 1.5 }}>
        Every outcome above is the exact frequency over all {(s.seedSpace / 1e6).toFixed(0)},000,000
        possible seeds — not a sample. Returns below 1× are losses, not wins.
      </div>
    </div>
  );
}
